import React, { useMemo } from "react";

const DEFAULT_SLEEPERS = ["🐶", "🐱", "🐰", "🦊", "🐻", "🐼", "🐨", "🐯", "🦁", "🐸"];

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

export default function AnimalSleep({
  className = "",
  count = 6,
  animals = DEFAULT_SLEEPERS,
  minTopPct = 80,
  maxTopPct = 92,
  seed = 2026,
} = {}) {
  const sleepers = useMemo(() => {
    const safeCount = clamp(count, 1, 16);
    const safeAnimals = Array.isArray(animals) && animals.length ? animals : DEFAULT_SLEEPERS;
    // tiny deterministic-ish shuffle based on seed
    let s = (Number(seed) || 0) >>> 0;
    const rand = () => {
      s = (s * 1664525 + 1013904223) >>> 0;
      return s / 4294967296;
    };

    const minTop = Math.max(0, Math.min(100, Number(minTopPct) || 0));
    const maxTop = Math.max(minTop, Math.min(100, Number(maxTopPct) || 100));

    return Array.from({ length: safeCount }).map((_, i) => {
      const left = Math.round(rand() * 96); // %
      const top = minTop + Math.round(rand() * Math.max(1, maxTop - minTop)); // %
      const size = 18 + Math.round(rand() * 18);
      const a = safeAnimals[Math.floor(rand() * safeAnimals.length)];
      const delay = -(rand() * 4);
      return { key: `sleep-${i}`, left, top, size, a, delay };
    });
  }, [count, animals, minTopPct, maxTopPct, seed]);

  return (
    <div className={`animal-sleep ${className}`} aria-hidden="true">
      <style>{`
        .animal-sleep {
          position: fixed;
          inset: 0;
          pointer-events: none;
          z-index: 1;
        }
        .animal-sleep__sleeper {
          position: absolute;
          left: var(--as-left, 50%);
          top: var(--as-top, 85%);
          font-size: var(--as-size, 26px);
          opacity: 0.95;
          filter: drop-shadow(0 10px 18px rgba(0,0,0,0.16));
          animation: animal-sleep-breathe 2.6s ease-in-out infinite alternate;
          animation-delay: var(--as-delay, 0s);
          text-shadow:
            0 1px 0 rgba(255, 255, 255, 0.25),
            0 2px 10px rgba(0, 0, 0, 0.20);
        }
        .animal-sleep__zzz {
          font-size: 0.72em;
          margin-left: 0.12em;
          opacity: 0.85;
          animation: animal-zzz 1.9s ease-in-out infinite;
        }
        @keyframes animal-sleep-breathe {
          from { transform: translate3d(0, 0, 0) scale(1); opacity: 0.9; }
          to { transform: translate3d(0, -3px, 0) scale(1.03); opacity: 1; }
        }
        @keyframes animal-zzz {
          0% { transform: translate3d(0, 0, 0); opacity: 0.35; }
          40% { opacity: 0.9; }
          100% { transform: translate3d(10px, -10px, 0); opacity: 0.1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .animal-sleep__sleeper, .animal-sleep__zzz { animation: none !important; }
        }
      `}</style>

      {sleepers.map((s) => (
        <span
          key={s.key}
          className="animal-sleep__sleeper"
          style={{
            "--as-left": `${s.left}%`,
            "--as-top": `${s.top}%`,
            "--as-size": `${s.size}px`,
            "--as-delay": `${s.delay}s`,
          }}
        >
          {s.a}
          <span className="animal-sleep__zzz">💤</span>
        </span>
      ))}
    </div>
  );
}

